import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Leaf, Tag, ArrowRight, Package } from "lucide-react";
import { api, resolveImage, formatDate } from "@/lib/api";
import { useLang } from "@/i18n";
import { usePageTitle } from "@/lib/useTitle";
import { SiteHeader } from "@/components/SiteHeader";
import { TagPill } from "@/lib/colors";
import { ImageGallery } from "@/components/ImageGallery";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";

export default function IngredientPage() {
  const { t } = useLang();
  const { id } = useParams();
  const [ingredient, setIngredient] = useState(null);
  const [batches, setBatches] = useState([]);
  const [status, setStatus] = useState("loading");

  usePageTitle(
    ingredient ? `${ingredient.name} | ${t("title.ingredientSuffix")}` : t("title.brand")
  );

  useEffect(() => {
    setStatus("loading");
    Promise.all([api.get(`/ingredients/${id}`), api.get(`/ingredients/${id}/batches`)])
      .then(([ing, bs]) => { setIngredient(ing.data); setBatches(bs.data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [id]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16"><div className="h-60 rounded-sm bg-secondary animate-pulse" /></div>
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-28 text-center" data-testid="ingredient-not-found">
          <h1 className="font-heading text-4xl sm:text-5xl text-ink">{t("ing.notfound")}</h1>
          <Link to="/archiv?tab=suroviny">
            <Button className="mt-8 rounded-sm bg-ink text-white hover:bg-sage-dark gap-2 font-semibold">
              <ArrowLeft className="h-4 w-4" /> {t("ing.notfound.back")}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader
        right={
          <Link to="/archiv?tab=suroviny" data-testid="back-ingredients-link" className="flex items-center gap-2 text-sm text-gray-500 hover:text-ink transition-colors duration-200 font-semibold">
            <ArrowLeft className="h-4 w-4" /> {t("ing.back")}
          </Link>
        }
      />

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
        <div className="animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sage/10 text-sage-dark font-data text-xs uppercase tracking-[0.15em] font-semibold">
            <Tag className="h-3.5 w-3.5" /> {ingredient.category}
          </div>
          <h1 className="font-heading text-4xl sm:text-6xl text-ink leading-[1.02] mt-4" data-testid="ingredient-name">{ingredient.name}</h1>
          {ingredient.tags && ingredient.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4" data-testid="ingredient-tags">
              {ingredient.tags.map((tg) => (<TagPill key={tg.id} tag={tg} className="text-sm px-3 py-1" testid={`ingredient-detail-tag-${tg.id}`} />))}
            </div>
          )}
          {ingredient.description && (
            <p className="mt-6 text-base sm:text-lg text-gray-700 leading-relaxed max-w-2xl" data-testid="ingredient-description">{ingredient.description}</p>
          )}
          {ingredient.supplier_url && (
            <a href={ingredient.supplier_url} target="_blank" rel="noopener noreferrer" data-testid="ingredient-supplier-button">
              <Button className="mt-7 h-12 px-6 rounded-sm bg-ink text-white hover:bg-sage-dark gap-2 font-semibold">
                {t("ing.verifySupplier")} <ExternalLink className="h-4 w-4" />
              </Button>
            </a>
          )}
        </div>

        {ingredient.images && ingredient.images.length > 0 && (
          <div className="mt-8 max-w-xl" data-testid="ingredient-gallery-wrap">
            <ImageGallery images={ingredient.images} alt={ingredient.name} testid="ingredient-gallery" />
          </div>
        )}

        <div className="mt-16 sm:mt-20">
          <div className="flex items-end justify-between mb-6">
            <div>
              <span className="font-data text-xs uppercase tracking-[0.2em] text-gray-500 font-semibold">{t("ing.reverse.kicker")}</span>
              <h2 className="font-heading text-2xl sm:text-4xl text-ink mt-1">{t("ing.reverse.title")}</h2>
            </div>
            <span className="font-data text-sm text-gray-400">{batches.length}</span>
          </div>

          {batches.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-sm" data-testid="no-batches">
              <Package className="h-9 w-9 mx-auto text-gray-300" strokeWidth={2} />
              <p className="mt-3 text-gray-600 text-sm">{t("ing.noBatches")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="ingredient-batches">
              {batches.map((b) => (
                <Link key={b.id} to={`/batch/${b.batch_code}`} data-testid={`ingredient-batch-${b.batch_code}`} className="group flex items-center gap-4 p-4 bg-white border border-border rounded-sm hover:border-sage transition-colors duration-200">
                  <div className="h-16 w-16 shrink-0 rounded-sm overflow-hidden bg-secondary">
                    {b.image_url ? (
                      <img src={resolveImage(b.image_url)} alt={b.product_name} className="h-full w-full object-cover" />
                    ) : (<div className="h-full w-full flex items-center justify-center text-gray-300"><Leaf className="h-5 w-5" strokeWidth={2} /></div>)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-data text-xs uppercase tracking-[0.12em] text-sage-dark font-semibold">{b.batch_code}</span>
                    <p className="font-heading text-lg text-ink truncate leading-tight">{b.product_name}</p>
                    <span className="font-data text-xs text-gray-400">{formatDate(b.production_date)}</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-sage-dark transition-colors duration-200 shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
